import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DrawerSetting, GridColumn } from '@grid/abstractions';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { QueryOptions } from 'odata-query';
import {
  Collection,
  GridItemCreateEvent,
  ResidencyJurisdiction,
} from '../../models';
import { UpsertResidencyJurisdictionComponent } from '../upsert-residency-jurisdiction/upsert-residency-jurisdiction.component';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-residency-jurisdictions-grid',
  templateUrl: './residency-jurisdictions-grid.component.html',
})
export class ResidencyJurisdictionsGridComponent {
  private translateService = inject(TranslateService);

  @Input({ required: true }) source: Collection<ResidencyJurisdiction> = {
    total: 0,
    items: [],
  };

  @Input() query!: Partial<QueryOptions<ResidencyJurisdiction>>;
  @Output() queryChange = new EventEmitter<
    Partial<QueryOptions<ResidencyJurisdiction>>
  >();

  @Output() create = new EventEmitter<
    GridItemCreateEvent<ResidencyJurisdiction>
  >();

  @Output() update = new EventEmitter<
    GridItemCreateEvent<ResidencyJurisdiction>
  >();

  drawerSetting: DrawerSetting<ResidencyJurisdiction> = {
    create: {
      title: this.translateService.instant(
        'admin.grid.createResidencyJurisdictionTitle'
      ),
      component: UpsertResidencyJurisdictionComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant(
        'admin.grid.updateResidencyJurisdictionTitle'
      ),
      component: UpsertResidencyJurisdictionComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.update.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('residencyJurisdiction.name'),
      key: 'country.name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('residencyJurisdiction.region'),
      key: 'region',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('residencyJurisdiction.modified'),
      key: 'modified',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('residencyJurisdiction.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  searchSetting: SearchSetting<ResidencyJurisdiction> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'country/name' as keyof ResidencyJurisdiction,
        operator: 'contains',
      },
    ],
  };

  queryHandler(
    residencyJurisdictionQuery: Partial<QueryOptions<ResidencyJurisdiction>>
  ): void {
    this.queryChange.emit(residencyJurisdictionQuery);
  }
}
